/*
 * Map provider configuration -- the frontend half of the seam described in
 * docs/google-maps-to-osm.md.
 *
 * One switch, `VUE_APP_MAP_PROVIDER`, decides which implementation renders a
 * map. Components ask for a map; they never name a vendor.
 *
 *   google  gmap-vue over the Google Maps JS API. Needs
 *           VUE_APP_PUBLIC_GOOGLE_PLACES_API_KEY. This is what production
 *           serves today.
 *   osm     Leaflet over OpenStreetMap raster tiles. Needs no key.
 *
 * Unset resolves to `google` when a Google key is configured and `osm`
 * otherwise, so no existing deployment moves and a fresh checkout with an
 * empty .env still renders a working, clickable map.
 */
const googleKey = process.env.VUE_APP_PUBLIC_GOOGLE_PLACES_API_KEY;
/*
 * The tile URL is deliberately NOT defaulted to tile.openstreetmap.org.
 *
 * The OSM Foundation's tile policy forbids using its tiles as the basemap of a
 * commercial product, forbids bulk downloading, and reserves the right to
 * block. Pointing production at it "temporarily" is how a product ends up
 * hard-blocked with no fallback. Production must set this to a paid provider
 * or a self-hosted renderer; see docs/google-maps-to-osm.md, "Tile and API
 * usage policy".
 *
 * With no tile URL configured the map still renders, still pans and zooms and
 * still shows and reports marker clicks -- it just draws a neutral grid
 * instead of imagery. That is the point: map interaction becomes testable with
 * no external service in the loop at all.
 */
export const MAP_TILE_URL = process.env.VUE_APP_MAP_TILE_URL || '';
export const MAP_TILE_ATTRIBUTION = process.env.VUE_APP_MAP_TILE_ATTRIBUTION ||
    '&copy; OpenStreetMap contributors';
export const MAP_TILE_MAX_ZOOM = Number(process.env.VUE_APP_MAP_TILE_MAX_ZOOM || 19);
/*
 * Points of interest near a venue -- the "zoeken in de buurt" panel.
 *
 * This is the one capability in the Google migration with no drop-in
 * replacement: Places nearbySearch has no OSM equivalent that is both free and
 * hosted. Overpass is the closest thing -- it queries raw OSM data, so
 * `amenity=restaurant within 500m` is a first-class query rather than a
 * product feature someone sells.
 *
 * EMPTY BY DEFAULT, on the same reasoning as MAP_TILE_URL above: overpass-api.de
 * is volunteer-funded infrastructure whose usage policy does not cover a
 * commercial product, and pointing production at it "temporarily" is how a
 * product ends up blocked with no fallback. With no URL configured the nearby
 * panel still renders its map, still centres on the venue and still zooms --
 * the filter toggles simply return nothing, which is honest and is also what
 * makes the panel testable with no external service.
 *
 * A self-hosted Overpass is the production answer; it runs off the same OSM
 * extract the Nominatim in docker/docker-compose.yml already imports. See
 * docs/osm-self-hosting.md.
 */
export const MAP_POI_URL = process.env.VUE_APP_MAP_POI_URL || '';
/** Metres around the venue that the nearby panel searches. */
export const MAP_POI_RADIUS = Number(process.env.VUE_APP_MAP_POI_RADIUS || 500);
/*
 * A CONFIGURED key, not merely a non-empty string.
 *
 * The deployed value of VUE_APP_PUBLIC_GOOGLE_PLACES_API_KEY on this stack is
 * the literal string 'disabled', which is truthy -- so a plain `googleKey ?`
 * test resolved to 'google', the Google JS API then failed to authorise, and
 * the search page rendered price pins over the grey "Google Maps is niet
 * correct geladen op deze pagina" panel instead of a map. Measured 2026-09-14.
 *
 * A Google Maps key is 'AIza' followed by 35 characters from the URL-safe
 * alphabet. Anything else -- 'disabled', an unsubstituted %%TOKEN%%, a
 * your-* placeholder, empty -- means "not configured", and not-configured must
 * mean OSM rather than a broken Google.
 *
 * This checks shape, not validity: a well-formed but revoked key still fails,
 * and the browser cannot know that in advance. It is the difference between a
 * misconfiguration (catchable here) and a dead credential (not).
 */
const GOOGLE_KEY_SHAPE = /^AIza[0-9A-Za-z_-]{35}$/;
const hasGoogleKey = GOOGLE_KEY_SHAPE.test(String(googleKey || ''));
export const MAP_PROVIDER = process.env.VUE_APP_MAP_PROVIDER ||
    (hasGoogleKey ? 'google' : 'osm');
export const isGoogleMaps = () => MAP_PROVIDER === 'google';
